import React, { useState, useEffect } from 'react';
import { Package, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { DeliveryPersonService } from '../services/deliveryPersonService';
import { useAuth } from '../utils/AuthContext';

interface SessionDeliveryPersonItem {
  id: string;
  item_sku: string;
  item_id?: string;
  quantity: number;
  unit_price: number;
  unit_cost: number;
  total_amount: number;
  order_references?: string;
  delivery_person_order_data?: any;
  created_at: string;
  updated_at: string;
  item?: {
    id: string;
    name: string;
    sku: string;
    barcode?: string;
    category?: string;
    brand?: string;
    shelves?: Array<{
      id: string;
      shelf_name: string;
    }>; // Array of shelf objects with IDs
    expiries?: Array<{
      id: string;
      expiry_date: string;
      reminder_days?: number;
      batch_number?: string;
      quantity?: number;
      notes?: string;
      is_reminded?: boolean;
    }>;
  };
  current_stock: number;
  average_cost: number;
  total_value: number;
  primary_image?: string;
}

interface DeliveryPersonScannerViewProps {
  sessionId: string;
  cartItems: any[]; // Current cart items to compare against
  onItemClick?: (item: SessionDeliveryPersonItem) => void;
  className?: string;
}

export const DeliveryPersonScannerView: React.FC<DeliveryPersonScannerViewProps> = ({
  sessionId,
  cartItems = [],
  onItemClick,
  className = ""
}) => {
  const { user } = useAuth();
  const [sessionItems, setSessionItems] = useState<SessionDeliveryPersonItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load session delivery person items
  useEffect(() => {
    if (sessionId) {
      loadSessionItems();
    }
  }, [sessionId]);

  // Refresh when cart changes to update item completion status
  useEffect(() => {
    // Force component re-render when cart items change
    // This ensures the filtering and progress calculations are updated
    if (sessionItems.length > 0) {
      console.log('🔄 DeliveryPersonScannerView: Cart changed, updating item completion status', {
        cartItemsCount: cartItems.length,
        sessionItemsCount: sessionItems.length
      });
    }
  }, [cartItems, sessionItems.length]);

  const loadSessionItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await DeliveryPersonService.getSessionDeliveryPersonItems(sessionId, user?.id);
      
      if (result.error) {
        setError(result.error);
        setSessionItems([]);
      } else {
        setSessionItems(result.data || []);
      }
    } catch (error: any) {
      console.error('Error loading session delivery person items:', error);
      setError('Failed to load session items');
      setSessionItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getCartQuantity = (sku: string) => {
    const cartItem = cartItems.find(item => item.sku === sku);
    return cartItem?.cartQuantity || 0;
  };

  const isItemCompleted = (item: SessionDeliveryPersonItem) => {
    const cartQuantity = getCartQuantity(item.item_sku);
    return cartQuantity >= item.quantity; // Consider completed if cart has equal or more quantity
  };

  const getRemainingQuantity = (item: SessionDeliveryPersonItem) => {
    const cartQuantity = getCartQuantity(item.item_sku);
    return Math.max(0, item.quantity - cartQuantity);
  };

  // Filter out completed items (items that are correctly added to cart)
  const remainingItems = sessionItems.filter(item => !isItemCompleted(item));
  const completedCount = sessionItems.length - remainingItems.length;
  const totalCount = sessionItems.length;

  // Debug logging for development
  if (sessionItems.length > 0) {
    console.log('📊 Scanner View Status:', {
      totalItems: totalCount,
      remainingItems: remainingItems.length,
      completedItems: completedCount,
      cartItems: cartItems.length,
      itemBreakdown: sessionItems.map(item => ({
        sku: item.item_sku,
        sessionQty: item.quantity,
        cartQty: getCartQuantity(item.item_sku),
        isCompleted: isItemCompleted(item)
      }))
    });
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border shadow-sm p-4 ${className}`}>
        <div className="flex items-center justify-center">
          <Package className="w-5 h-5 animate-pulse text-gray-400 mr-2" />
          <span className="text-gray-600">Loading delivery items...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 rounded-lg border border-red-200 p-4 ${className}`}>
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  if (sessionItems.length === 0) {
    return null; // Don't show anything if no session items
  }

  if (remainingItems.length === 0) {
    return (
      <div className={`bg-green-50 rounded-lg border border-green-200 p-4 ${className}`}>
        <div className="flex items-center justify-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
          <span className="text-green-800 font-medium">
            All delivery items completed! ({totalCount}/{totalCount})
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border shadow-sm ${className}`}>
      {/* Fixed Left Counter */}
      <div className="flex items-center">
        <div className="flex-shrink-0 bg-blue-600 text-white px-4 py-3 rounded-l-lg">
          <div className="text-center">
            <div className="text-2xl font-bold">{remainingItems.length}</div>
            <div className="text-xs opacity-90">remaining</div>
          </div>
        </div>

        {/* Horizontal Scrollable Items */}
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-3 p-3" style={{ minWidth: 'max-content' }}>
            {remainingItems.map((item) => {
              const remainingQty = getRemainingQuantity(item);
              const cartQty = getCartQuantity(item.item_sku);
              
              return (
                <div
                  key={item.id}
                  className="flex-shrink-0 w-40 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => onItemClick?.(item)}
                >
                  {/* Item Image */}
                  <div className="w-full h-24 bg-gray-100 rounded-t-lg overflow-hidden">
                    {item.primary_image ? (
                      <img
                        src={item.primary_image}
                        alt={item.item?.name || item.item_sku}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="p-3">
                    <h4 className="font-medium text-sm text-gray-900 truncate mb-1">
                      {item.item?.name || item.item_sku}
                    </h4>
                    <p className="text-xs text-gray-500 mb-2">SKU: {item.item_sku}</p>
                    
                    {/* Shelf Tags */}
                    {item.item?.shelves && item.item.shelves.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.item.shelves.slice(0, 2).map((shelf: any, index: number) => (
                          <span 
                            key={shelf.id || index}
                            className="inline-block bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-medium"
                          >
                            {shelf.shelf_name}
                          </span>
                        ))}
                        {item.item.shelves.length > 2 && (
                          <span className="text-xs text-purple-600">
                            +{item.item.shelves.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Quantity Status */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-blue-600">Have:</span>
                        <span className={cartQty > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'}>
                          {cartQty}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-red-600">Missing:</span>
                        <span className="font-bold text-red-600">{remainingQty}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Need:</span>
                        <span className="font-medium">{item.quantity}</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(100, (cartQty / item.quantity) * 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Stock Level Indicator */}
                    {item.current_stock !== undefined && (
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStockLevelColor(item.current_stock)}`}>
                          Stock: {item.current_stock}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Completion Status */}
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 rounded-r-lg border-l">
          <div className="text-center">
            <div className="text-sm font-medium text-gray-900">
              {completedCount}/{totalCount}
            </div>
            <div className="text-xs text-gray-500">completed</div>
            {completedCount > 0 && (
              <CheckCircle className="w-4 h-4 text-green-500 mx-auto mt-1" />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function getStockLevelColor(stock: number) {
    if (stock < 0) return 'text-red-600 bg-red-50';
    if (stock === 0) return 'text-orange-600 bg-orange-50';
    if (stock <= 5) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  }
};
